/**
 * Browser authentication lifecycle for FluxAccounts and retained provider callbacks. Historical
 * storage keys and cookie sessions remain readable for account continuity; an issuer exchange is
 * followed by an independent API session check before marking the user authenticated.
 *
 * @module apps/web/src/contexts/AuthContext
 */

import { createContext, useContext, useEffect, useEffectEvent, useRef, useState, ReactNode } from 'react';
import type { AccountProfile } from '@fluxability/types';
import { useUserStore } from '@/stores/userStore';
import { api } from '@/lib/api';
import { invalidateSchedulingSession } from '@/lib/browserScheduleFlow';
import { resetBrowserScheduling } from '@/lib/browserScheduling';
import { resetBrowserInference } from '@/lib/browserInference';
import { retryAuthMaintenance } from '@/lib/authMaintenance';
import { clearAvatarCache } from '@/lib/avatarImage';
import { completeSaIdLogin, startSaIdLogin } from '@/lib/saidClient';
import { SignInError, clearSignInFailure, rememberSignInFailure } from '@/lib/signInNotice';

/** Session readiness and explicit login/link/logout actions exposed to the application tree. isAuthenticated becomes true only after the API session check succeeds. */
interface AuthContextType {
  /** Whether the planner API session was confirmed by the provider lifecycle. */
  isAuthenticated: boolean;
  /** Whether initial session/callback processing is still pending. */
  isLoading: boolean;
  /** Failure shown on the sign-in surface when callback or session hydration fails. */
  authError: string | null;
  /** Start the approved-issuer OAuth flow with an optional local return destination. */
  loginWithSaId: (returnTo?: string) => Promise<void>;
  /** Start a retained provider linking flow; the server remains responsible for identity binding. */
  linkOAuthProvider: (provider: 'zoho' | 'github', returnTo?: string) => Promise<void>;
  /** End the application session and clear the local authentication projection. */
  logout: () => Promise<void>;
}

/** Nullable authentication context shared by the provider and useAuth. The null default detects consumers mounted outside AuthProvider; the context itself performs no session validation. */
const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Hydrate the current API session and expose login, email linking, and logout through context.
 * FluxAccounts callbacks retain their exchanged token across explicit maintenance retries and
 * verify the resulting session user. Stale bearer credentials can fall back to an existing HttpOnly
 * cookie session; failed callbacks and logout clear local identity state.
 *
 * @param children - Application subtree that requires authentication state.
 */
export function AuthProvider({
  children,
}: {
  /** Controls or content supplied by the owning view. */
  children: ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  // StrictMode replays effects. A one-use authorization code must have one owner
  // for this provider's lifetime, including the API handoff and session check.
  const sessionStarted = useRef(false);
  const setUser = useUserStore(s => s.setUser);
  const clearUser = useUserStore(s => s.clearUser);

  const startSessionCheck = useEffectEvent(() => {
    checkSession();
  });
  useEffect(() => {
    if (sessionStarted.current) return;
    sessionStarted.current = true;
    startSessionCheck();
  }, []);

  const checkSession = async () => {
    try {
      try {
        if (window.location.pathname.startsWith('/auth/said/callback')) {
          const saidCallback = await completeSaIdLogin();
          if (saidCallback?.token) {
            const response = await retryAuthMaintenance(() =>
              api.auth.saidExchange({ token: saidCallback.token })
            );
            if (!response.success || !response.data?.user) {
              throw new SignInError(
                response.error?.code || 'SIGN_IN_FAILED',
                response.error?.message || 'Could not complete FluxAccounts sign in'
              );
            }
            if (response.data.token)
              localStorage.setItem('flexiplanner-session-token', response.data.token);
            else localStorage.removeItem('flexiplanner-session-token');
            const confirmed = await retryAuthMaintenance(() => api.auth.session());
            if (
              !confirmed.success ||
              !confirmed.data?.user ||
              confirmed.data.user.id !== response.data.user.id
            ) {
              throw new Error(
                'Your sign-in succeeded, but the browser could not confirm the secure session. Please try again.'
              );
            }
            applyUser(confirmed.data.user);
            setIsAuthenticated(true);
            window.location.replace(saidCallback.returnTo || '/teams');
            return;
          }
        }

        const providerCallback = consumeProviderOAuthCallback();
        if (providerCallback?.token) {
          localStorage.setItem('flexiplanner-session-token', providerCallback.token);
          const response = await api.auth.session();
          if (!response.success || !response.data?.user) {
            throw new Error(response.error?.message || 'Could not complete provider sign in');
          }
          applyUser(response.data.user);
          setIsAuthenticated(true);
          window.location.replace(providerCallback.returnTo || '/teams');
          return;
        }
        if (providerCallback?.error) {
          throw new Error(providerCallback.error);
        }
      } catch (error) {
        setAuthError(
          error instanceof Error ? error.message : 'Could not complete sign-in. Please try again.'
        );
        rememberSignInFailure(error);
        localStorage.removeItem('flexiplanner-session-token');
        clearUser();
        setIsAuthenticated(false);
        return;
      }

      // Existing Rust sessions are HttpOnly cookies on the API host. Always hydrate
      // with credentials, including when no legacy bearer token exists.
      let response = await api.auth.session();
      if (
        !response.success &&
        response.error?.code === 'UNAUTHORIZED' &&
        localStorage.getItem('flexiplanner-session-token')
      ) {
        localStorage.removeItem('flexiplanner-session-token');
        response = await api.auth.session();
      }
      if (response.success && response.data?.user) {
        applyUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('flexiplanner-session-token');
        clearUser();
        setIsAuthenticated(false);
      }
    } catch (error) {
      setAuthError(
        error instanceof Error ? error.message : 'Could not check your session. Please try again.'
      );
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithSaId = async (returnTo = '/teams') => {
    clearSignInFailure();
    await startSaIdLogin(returnTo);
  };

  const linkOAuthProvider = async (
    provider: 'zoho' | 'github',
    returnTo = window.location.pathname || '/settings'
  ) => {
    const response =
      provider === 'zoho'
        ? await api.auth.zohoLinkAuthUrl(returnTo)
        : await api.auth.githubLinkAuthUrl(returnTo);
    if (!response.success || !response.data?.url) {
      throw new Error(response.error?.message || `${provider} account linking is not available.`);
    }
    window.location.href = response.data.url;
  };

  const logout = async () => {
    // Logout intent immediately destroys private compute, before the network round trip.
    invalidateSchedulingSession();
    resetBrowserScheduling();
    resetBrowserInference();
    try {
      await api.auth.logout();
    } finally {
      localStorage.removeItem('flexiplanner-session-token');
      localStorage.removeItem('sahast-session-token');
      clearSignInFailure();
      clearAvatarCache();
      clearUser();
      setIsAuthenticated(false);
    }
  };

  const applyUser = (
    user: AccountProfile & {
      /** Legacy camelCase display name accepted from older session payloads. */
      displayName?: string;
    }
  ) => {
    setUser({
      id: user.id || user.user_id,
      // Company tenants omit the email; the store keeps '' as it does after onboarding.
      email: user.email || '',
      displayName: user.display_name || user.displayName,
      avatarUrl: user.avatar_url ?? null,
      timezone: user.timezone,
      onboardingCompleted: Boolean(user.onboarding_completed),
      settings: serverSettingsToClient(user.settings),
    });
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, authError, loginWithSaId, linkOAuthProvider, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Read authentication state and actions from the nearest AuthProvider. Throws when called outside the provider, so missing application wiring fails explicitly.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/** Translate the server’s preference field names into the browser-store shape. Absent settings remain undefined so the store can preserve its own defaults. */
function serverSettingsToClient(settings: AccountProfile['settings']) {
  if (!settings) return undefined;
  return {
    theme: settings.theme,
    defaultView: settings.default_view,
    workHours: settings.work_hours,
    workDays: settings.work_days,
  };
}

/** Read retained Zoho/GitHub callback material from the URL fragment only on their callback paths, then remove the fragment from history. This parser is not issuer validation; the enclosing session check decides whether the token is accepted. */
function consumeProviderOAuthCallback(): {
  /** Provider callback credential read from the URL fragment before session exchange. */
  token?: string;
  /** Provider callback failure text when authorization did not succeed. */
  error?: string;
  /** Local destination restored after processing the provider callback. */
  returnTo?: string;
} | null {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname;
  if (!path.startsWith('/auth/zoho/callback') && !path.startsWith('/auth/github/callback'))
    return null;
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const token = params.get('oauth_token') || undefined;
  const error = params.get('oauth_error') || undefined;
  const returnTo = params.get('return_to') || '/teams';
  window.history.replaceState({}, document.title, path);
  return { token, error, returnTo: returnTo.startsWith('/') ? returnTo : '/teams' };
}
